import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import type { User, Session } from "@mash/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import { create } from "zustand";

import { getAppUrl, setLoggedInHint } from "../lib/hostname";
import { syncTimezone, clearTimezoneCache } from "../lib/syncTimezone";
import { inviteLinkApi } from "../services/userApi";

let supabase!: SupabaseClient;

interface AuthState {
  user: User | null;
  session: Session | null;
  role: string | null;
  loading: boolean;
  error: string | null;
  _listenerInitialized: boolean;
  _fetchingUser: boolean;
  isAdmin: () => boolean;

  // Actions
  initialize: (client: SupabaseClient) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<void>;
  signInWithGoogle: (inviteCode?: string) => Promise<void>;
  signInWithApple: (inviteCode?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  _updateSession: (
    session: {
      access_token: string;
      refresh_token: string;
      expires_at?: number;
    } | null,
  ) => void;
  _fetchAndSetUser: (
    userId: string,
    session: {
      access_token: string;
      refresh_token: string;
      expires_at?: number;
      user?: { id: string; email?: string; created_at: string };
    },
  ) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  role: null,
  loading: true,
  error: null,
  _listenerInitialized: false,
  _fetchingUser: false,

  isAdmin: () => get().role === "admin",

  // Internal: update session tokens without refetching user
  _updateSession: session => {
    if (session) {
      const expiresAt = session.expires_at || 0;
      const now = Math.floor(Date.now() / 1000);
      const timeToExpiry = expiresAt - now;

      set({
        session: {
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
          expiresAt: expiresAt,
        },
      });
      console.log("[Auth] Session tokens updated:", {
        expiresAt: new Date(expiresAt * 1000).toISOString(),
        timeToExpiry: `${Math.floor(timeToExpiry / 60)} minutes`,
      });
    } else {
      set({ user: null, session: null, role: null });
      console.log("[Auth] Session cleared");
    }
  },

  // Internal: fetch user profile and set full auth state.
  // Handles pending invite acceptance before reading the profile so
  // invite_accepted is already true by the time we set user state.
  // Uses _fetchingUser mutex to prevent concurrent calls from racing.
  _fetchAndSetUser: async (userId, session) => {
    if (get()._fetchingUser) return; // Another fetch is already running
    set({ _fetchingUser: true });

    try {
      // Ensure session tokens are set so API calls have auth
      get()._updateSession(session);

      // Accept pending invite in background — don't block auth startup.
      const pendingJoinCode = localStorage.getItem("pendingJoinCode");
      if (pendingJoinCode) {
        localStorage.removeItem("pendingJoinCode");
        console.log("[Auth] Accepting pending invite:", pendingJoinCode);
        inviteLinkApi
          .accept(pendingJoinCode)
          .then(() =>
            console.log("[Auth] Pending invite accepted successfully"),
          )
          .catch((err: any) =>
            console.warn("[Auth] Invite accept result:", err?.message),
          );

        // Redirect back to the app if OAuth was triggered from guest mode
        const pendingAppId = localStorage.getItem("pendingGuestAppId");
        if (pendingAppId) {
          localStorage.removeItem("pendingGuestAppId");
          setTimeout(() => {
            window.location.href = `/app/${pendingAppId}`;
          }, 100);
        }
      }

      // Now fetch profile — invite_accepted is up-to-date
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name, avatar_url, invite_accepted, role")
        .eq("id", userId)
        .single();

      console.log(
        "[Auth] _fetchAndSetUser() setting user — invite_accepted from DB:",
        profile?.invite_accepted,
        "current store:",
        get().user?.inviteAccepted,
      );
      const expiresAt = session.expires_at || 0;
      const now = Math.floor(Date.now() / 1000);
      const timeToExpiry = expiresAt - now;

      set({
        user: {
          id: userId,
          email: session.user?.email || "",
          displayName: profile?.display_name,
          avatarUrl: profile?.avatar_url,
          createdAt: session.user?.created_at || "",
          inviteAccepted: profile?.invite_accepted ?? true,
        },
        role: profile?.role ?? null,
        session: {
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
          expiresAt: expiresAt,
        },
        loading: false,
      });
      console.log("[Auth] User authenticated:", {
        email: session.user?.email,
        inviteAccepted: profile?.invite_accepted,
        inviteAcceptedFinal: profile?.invite_accepted ?? true,
        hadPendingJoinCode: !!pendingJoinCode,
        expiresAt: new Date(expiresAt * 1000).toISOString(),
        timeToExpiry: `${Math.floor(timeToExpiry / 3600)} hours`,
      });

      // Set cross-subdomain cookie so landing page can redirect to app
      setLoggedInHint(true);

      // Sync browser timezone to backend (fire-and-forget, cached in localStorage)
      syncTimezone();
    } catch (error) {
      console.error("[Auth] Failed to fetch user:", error);
      set({ user: null, session: null, role: null, loading: false });
    } finally {
      set({ _fetchingUser: false });
    }
  },

  initialize: async (client: SupabaseClient) => {
    supabase = client;
    const state = get();

    // Set up auth state change listener ONCE
    if (!state._listenerInitialized) {
      set({ _listenerInitialized: true });

      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log("[Auth] Auth state change:", event);

        switch (event) {
          case "SIGNED_OUT":
            set({ user: null, session: null, role: null, loading: false });
            break;

          case "SIGNED_IN":
            // Invite accept + redirect logic is handled inside _fetchAndSetUser
            // (which also serializes concurrent calls via _fetchingUser mutex)
            if (session?.user) {
              get()._fetchAndSetUser(session.user.id, session);
            }
            break;

          case "INITIAL_SESSION":
            if (session?.user) {
              get()._fetchAndSetUser(session.user.id, session);
            } else {
              set({ user: null, session: null, role: null, loading: false });
            }
            break;

          case "TOKEN_REFRESHED":
            // Critical: Update stored tokens when Supabase refreshes them
            if (session) {
              get()._updateSession(session);
            }
            break;

          case "USER_UPDATED":
            // Refresh user data
            if (session?.user) {
              get()._fetchAndSetUser(session.user.id, session);
            }
            break;
        }
      });

      // Set up app lifecycle listeners for session refresh on resume
      // This prevents logout after inactivity by refreshing expired JWTs

      // Native apps: Listen for app resume
      if (Capacitor.isNativePlatform()) {
        CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
          if (isActive) {
            console.log("[Auth] App resumed, checking session...");
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              if (session?.user) {
                console.log("[Auth] Session valid on resume");
                // Force a refresh if close to expiry (within 5 minutes)
                const expiresAt = session.expires_at || 0;
                const now = Math.floor(Date.now() / 1000);
                const timeToExpiry = expiresAt - now;
                if (timeToExpiry < 300) {
                  console.log("[Auth] Token close to expiry, refreshing...");
                  await supabase.auth.refreshSession();
                }
              } else {
                console.log("[Auth] No session on resume");
              }
            } catch (error) {
              console.error("[Auth] Session check on resume failed:", error);
            }
          }
        });
        console.log("[Auth] Native app lifecycle listener registered");

        // Listen for deep link (OAuth callback) on native
        CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
          console.log("[Auth] App URL opened:", url);

          // Handle OAuth callback: app.forgeapps.main://oauth-callback#access_token=...
          if (url.startsWith("app.forgeapps.main://oauth-callback")) {
            // Close the system browser
            Browser.close().catch(() => {});

            // Extract tokens from the URL fragment
            const hashPart = url.split("#")[1];
            if (hashPart) {
              const params = new URLSearchParams(hashPart);
              const accessToken = params.get("access_token");
              const refreshToken = params.get("refresh_token");

              if (accessToken && refreshToken) {
                console.log("[Auth] Setting session from OAuth callback");
                const { error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                });
                if (error) {
                  console.error(
                    "[Auth] Failed to set session from OAuth:",
                    error,
                  );
                  set({ error: error.message, loading: false });
                }
              }
            }
          }
        });
        console.log("[Auth] Native deep link listener registered");
      }

      // Web: Listen for visibility change
      if (!Capacitor.isNativePlatform()) {
        document.addEventListener("visibilitychange", async () => {
          if (!document.hidden) {
            console.log("[Auth] Page visible, checking session...");
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              if (session?.user) {
                console.log("[Auth] Session valid on visibility change");
                // Force a refresh if close to expiry (within 5 minutes)
                const expiresAt = session.expires_at || 0;
                const now = Math.floor(Date.now() / 1000);
                const timeToExpiry = expiresAt - now;
                if (timeToExpiry < 300) {
                  console.log("[Auth] Token close to expiry, refreshing...");
                  await supabase.auth.refreshSession();
                }
              } else {
                console.log("[Auth] No session on visibility change");
              }
            } catch (error) {
              console.error(
                "[Auth] Session check on visibility change failed:",
                error,
              );
            }
          }
        });
        console.log("[Auth] Web visibility listener registered");
      }

      console.log("[Auth] Auth state listener initialized");
    }

    // Get current session
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        await get()._fetchAndSetUser(session.user.id, session);
      } else {
        set({ user: null, session: null, role: null, loading: false });
      }
    } catch (error) {
      console.error("[Auth] Initialization failed:", error);
      set({ user: null, session: null, role: null, loading: false });
    }
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null });

    try {
      console.log("[Auth] Signing in with email:", email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log("[Auth] Sign in response:", data, error);

      if (error) {
        set({ error: error.message, loading: false });
        return;
      }

      if (data.user && data.session) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name, avatar_url, invite_accepted, role")
          .eq("id", data.user.id)
          .single();

        console.log(
          "[Auth] signIn() setting user — invite_accepted from DB:",
          profile?.invite_accepted,
        );
        set({
          user: {
            id: data.user.id,
            email: data.user.email || "",
            displayName: profile?.display_name,
            avatarUrl: profile?.avatar_url,
            createdAt: data.user.created_at,
            inviteAccepted: profile?.invite_accepted ?? true,
          },
          role: profile?.role ?? null,
          session: {
            accessToken: data.session.access_token,
            refreshToken: data.session.refresh_token,
            expiresAt: data.session.expires_at || 0,
          },
          loading: false,
        });
      }
    } catch (error) {
      console.error("[Auth] Sign in error:", error);
      set({
        error: error instanceof Error ? error.message : "Sign in failed",
        loading: false,
      });
    }
  },

  signUp: async (email: string, password: string, displayName?: string) => {
    set({ loading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: displayName } },
      });

      if (error) {
        set({ error: error.message, loading: false });
        return;
      }

      if (data.user) {
        // Create profile
        await supabase
          .from("profiles")
          .insert({
            id: data.user.id,
            display_name: displayName || email.split("@")[0],
          });

        // If email confirmation is disabled, sign in directly
        if (data.session) {
          set({
            user: {
              id: data.user.id,
              email: data.user.email || "",
              displayName: displayName,
              createdAt: data.user.created_at,
            },
            session: {
              accessToken: data.session.access_token,
              refreshToken: data.session.refresh_token,
              expiresAt: data.session.expires_at || 0,
            },
            loading: false,
          });
        } else {
          set({ loading: false });
        }
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Sign up failed",
        loading: false,
      });
    }
  },

  signInWithGoogle: async (inviteCode?: string) => {
    set({ error: null });

    // Store join code so we can accept the invite after OAuth redirect
    if (inviteCode) {
      localStorage.setItem("pendingJoinCode", inviteCode);
    }

    const isNative = Capacitor.isNativePlatform();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // Admin dashboard lives on its own origin; bring OAuth back here, not the app
        // subdomain. Land on /login (a stable route that doesn't redirect while auth is
        // bootstrapping) so supabase can consume the callback URL before we navigate; the
        // root route immediately redirects to /admin and would strip the OAuth params.
        redirectTo: isNative
          ? "app.forgeapps.main://oauth-callback"
          : `${window.location.origin}/login`,
        skipBrowserRedirect: isNative,
        queryParams: { prompt: "select_account" },
      },
    });

    if (error) {
      console.error("[Auth] Google OAuth error:", error.message, error);
      localStorage.removeItem("pendingJoinCode");
      set({ error: error.message });
      return;
    }

    // On native, open the OAuth URL in the system browser
    if (isNative && data?.url) {
      await Browser.open({ url: data.url });
    }
  },

  signInWithApple: async (inviteCode?: string) => {
    set({ error: null });

    if (inviteCode) {
      localStorage.setItem("pendingJoinCode", inviteCode);
    }

    const isNative = Capacitor.isNativePlatform();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        // Admin dashboard lives on its own origin; bring OAuth back here, not the app
        // subdomain. Land on /login (a stable route that doesn't redirect while auth is
        // bootstrapping) so supabase can consume the callback URL before we navigate; the
        // root route immediately redirects to /admin and would strip the OAuth params.
        redirectTo: isNative
          ? "app.forgeapps.main://oauth-callback"
          : `${window.location.origin}/login`,
        skipBrowserRedirect: isNative,
      },
    });

    if (error) {
      console.error("[Auth] Apple OAuth error:", error.message, error);
      localStorage.removeItem("pendingJoinCode");
      set({ error: error.message });
      return;
    }

    if (isNative && data?.url) {
      await Browser.open({ url: data.url });
    }
  },

  resetPassword: async (email: string) => {
    set({ error: null });

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getAppUrl()}/reset-password`,
    });

    if (error) {
      set({ error: error.message });
      throw error;
    }
  },

  signOut: async () => {
    // Clear state immediately so the UI redirects to login without waiting
    set({ user: null, session: null, role: null, loading: false });
    clearTimezoneCache();
    setLoggedInHint(false);

    try {
      console.log("[Auth] Signing out local session only");
      await supabase.auth.signOut({ scope: "local" });
      console.log("[Auth] Local signout complete");
    } catch (error) {
      // Session already cleared above — just log the error
      console.error("[Auth] Signout error:", error);
    }
  },

  clearError: () => set({ error: null }),
}));
